<template>
  <div>
    <Head :title="$t('message.loan.edit_loan')" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-indigo-400 hover:text-indigo-600" href="/loans/loans">
        {{ $t('message.loan.loan') }}
      </Link>
      <span class="text-indigo-400 font-medium">/</span>
      {{ loan.user ? loan.user.name : 'N/A' }}
    </h1>
    <trashed-message v-if="loan.deleted_at" class="mb-6" @restore="restore">
      {{ $t('message.general.this_loan_has_been_deleted') }}
    </trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <!-- Status (Admin/HR/Exec/Head of HR only) -->
          <select-input v-if="can_approve" v-model="form.status" :error="form.errors.status" class="pb-8 pr-6 w-full" :label="$t('message.general.status')">
            <option value="pending">{{ $t('message.loan.pending') }}</option>
            <option value="reviewed" v-if="canReview">{{ $t('message.loan.reviewed') }}</option>
            <option value="approved" v-if="canApprove">{{ $t('message.loan.approved') }}</option>
            <option value="finalised" v-if="canFinalise">{{ $t('message.loan.finalised') }}</option>
            <option value="rejected">{{ $t('message.loan.rejected') }}</option>
          </select-input>

          <!-- Disbursement Date (Admin/HR/Finance only, when approving) -->
          <date-picker v-if="can_approve && (form.status === 'finalised' || form.status === 'approved')" v-model="form.disbursement_date" :error="form.errors.disbursement_date" class="pb-8 pr-6 w-full" :label="$t('message.loan.disbursement_date')" :min="today" :required="false" />

          <!-- Rejection Reason (Admin/HR/Finance only, when rejecting) -->
          <textarea-input v-if="can_approve && form.status === 'rejected'" v-model="form.rejection_reason" :error="form.errors.rejection_reason" class="pb-8 pr-6 w-full" :label="$t('message.loan.rejection_reason')" />

          <!-- Loan Type -->
          <select-input v-model="form.loan_type_id" :error="form.errors.loan_type_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.loan_type')" :disabled="!can_edit || form.status !== 'pending'" @change="updateLoanLimits">
            <option :value="null" />
            <option v-for="loanType in loan_types" :key="loanType.id" :value="loanType.id">{{ loanType.name }} ({{ loanType.interest_rate }}%)</option>
          </select-input>

          <!-- Branch -->
          <select-input v-model="form.branch" :error="form.errors.branch" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.branches.branch')" :disabled="!can_edit || form.status !== 'pending'">
            <option :value="null" />
            <option v-for="branch in branches" :key="branch.id" :value="branch.id">{{ branch.name }}</option>
          </select-input>

          <!-- Level -->
          <select-input v-model="form.level" :error="form.errors.level" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.level')" :disabled="!can_edit || form.status !== 'pending'">
            <option :value="null" />
            <option v-for="level in levels" :key="level.id" :value="level.id">
              {{ level.name }}
            </option>
          </select-input>

          <!-- Net Salary -->
          <text-input v-model="form.net_salary" :error="form.errors.net_salary" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.staffs.net_salary')" type="number" min="0" step="0.01" :disabled="!can_edit || form.status !== 'pending'" @input="updateMaxEligibleAmount" />

          <!-- Loan Amount -->
          <text-input v-model="form.loan_amount" :error="form.errors.loan_amount" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.amount')" type="number" step="0.01" :min="minLoanAmount" :max="maxLoanAmount" :disabled="!can_edit || form.status !== 'finalised'" />

          <!-- Loan Tenure -->
          <text-input v-model="form.loan_tenure" :error="form.errors.loan_tenure" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.duration')" type="number" min="1" max="60" :disabled="!can_edit || form.status !== 'finalised'" @input="updateMaxEligibleAmount" />

          <!-- Purpose -->
          <textarea-input v-model="form.purpose" :error="form.errors.purpose" class="pb-8 pr-6 w-full" :label="$t('message.loan.purpose')" :disabled="!can_edit || form.status !== 'pending'" />

          <!-- Other Loans with Nafa -->
          <select-input v-model="form.other_loans_with_nafa_id" :error="form.errors.other_loans_with_nafa_id" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.other_loans_with_nafa')" :disabled="!can_edit || form.status !== 'pending'">
            <option :value="null">None</option>
            <option v-for="loanType in loan_types" :key="loanType.id" :value="loanType.id">{{ loanType.name }}</option>
          </select-input>

          <!-- Outstanding Nafa Loan Balance -->
          <text-input v-model="form.outstanding_nafa_loan_balance" :error="form.errors.outstanding_nafa_loan_balance" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.outstanding_nafa_loan_balance')" type="number" min="0" step="0.01" :disabled="!can_edit || form.status !== 'pending'" />

          <!-- Outstanding Bank Loan Balance -->
          <text-input v-model="form.outstanding_bank_loan_balance" :error="form.errors.outstanding_bank_loan_balance" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.outstanding_bank_loan_balance')" type="number" min="0" step="0.01" :disabled="!can_edit || form.status !== 'pending'" />

          <!-- Other Loans with Bank -->
          <text-input v-model="form.other_loans_with_bank" :error="form.errors.other_loans_with_bank" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.other_loans_with_bank')" :disabled="!can_edit || form.status !== 'pending'" />

          <!-- Wallet Number (visible for approved loans) -->
          <text-input v-model="form.wallet_number" :error="form.errors.wallet_number" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.wallet_number')" type="text" maxlength="7" pattern="[0-9]{7}" title="Wallet number must be exactly 7 digits" @input="validateWalletNumber" :disabled="!can_edit" />

          <!-- Funds Transfer Status (visible for approved loans) -->
          <select-input v-if="form.status === 'approved' && can_approve" v-model="form.funds_transfer_status" :error="form.errors.funds_transfer_status" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('message.loan.funds_transfer_status')">
            <option value="pending">{{ $t('message.loan.pending') }}</option>
            <option value="processing">{{ $t('message.loan.processing') }}</option>
            <option value="completed">{{ $t('message.loan.completed') }}</option>
            <option value="failed">{{ $t('message.loan.failed') }}</option>
          </select-input>

          <!-- Funds Received At (read-only, visible when funds_transfer_status is completed) -->
          <div v-if="form.status === 'approved' && form.funds_transfer_status === 'completed'" class="pb-8 pr-6 w-full lg:w-1/2">
            <label class="form-label">{{ $t('message.loan.funds_received_at') }}</label>
            <div class="p-2 bg-gray-100 rounded">
              {{ form.funds_received_at ? formatDate(form.funds_received_at) : 'N/A' }}
            </div>
          </div>

          <!-- Loan Calculation Summary -->
          <div v-if="canCalculatePayment" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.payment_summary') }}</h3>
            <div class="p-4 bg-gray-50 rounded-md">
              <div class="grid gap-4 grid-cols-1 md:grid-cols-2">
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.monthly_payment') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(monthlyPayment) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.total_payment') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(totalPayment) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.total_interest') }}:</p>
                  <p class="font-semibold">{{ formatCurrency(totalInterest) }}</p>
                </div>
                <div>
                  <p class="text-gray-600">{{ $t('message.loan.interest_rate') }}:</p>
                  <p class="font-semibold">{{ selectedLoanType ? selectedLoanType.interest_rate : 0 }}%</p>
                </div>
              </div>

              <!-- Repayment percentage warning -->
              <div v-if="repaymentPercentage > 40 && can_edit" class="mt-4 p-3 text-red-700 bg-red-100 rounded-md">
                <p class="font-semibold">Warning: Monthly repayment is {{ Math.round(repaymentPercentage) }}% of your net salary.</p>
                <p>Loans with monthly repayments exceeding 40% of your net salary will not be approved.</p>
                <p class="mt-1">Maximum eligible loan amount: {{ formatCurrency(maxEligibleLoanAmount) }}</p>
              </div>
              <div v-else-if="repaymentPercentage > 0 && can_edit" class="mt-4 p-3 text-green-700 bg-green-100 rounded-md">
                <p class="font-semibold">Monthly repayment is {{ Math.round(repaymentPercentage) }}% of your net salary.</p>
                <p v-if="repaymentPercentage > 30 && repaymentPercentage <= 40" class="mt-1">You're approaching the 40% limit. Consider a longer tenure or lower loan amount.</p>
              </div>
            </div>
          </div>

          <!-- Review Information -->
          <div v-if="form.status !== 'pending' && loan.reviewer" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.review_info') }}</h3>
            <div class="bg-blue-50 p-4 rounded-md">
              <p class="text-gray-700">
                {{ $t('message.loan.reviewed_by') }}: <span class="font-semibold">{{ loan.reviewer.name }}</span>
              </p>
              <p v-if="loan.reviewed_at" class="text-gray-700">
                {{ $t('message.loan.reviewed_at') }}: <span class="font-semibold">{{ formatDate(loan.reviewed_at) }}</span>
              </p>
            </div>
          </div>

          <!-- Approval Information -->
          <div v-if="(form.status === 'approved' || form.status === 'finalised') && loan.approver" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.approval_info') }}</h3>
            <div class="p-4 bg-green-50 rounded-md">
              <p class="text-gray-700">
                {{ $t('message.loan.approved_by') }}: <span class="font-semibold">{{ loan.approver.name }}</span>
              </p>
              <p v-if="loan.approved_at" class="text-gray-700">
                {{ $t('message.loan.approved_at') }}: <span class="font-semibold">{{ formatDate(loan.approved_at) }}</span>
              </p>
              <p v-if="form.disbursement_date" class="text-gray-700">
                {{ $t('message.loan.disbursement_date') }}: <span class="font-semibold">{{ formatDate(form.disbursement_date) }}</span>
              </p>
            </div>
          </div>

          <!-- Finalisation Information -->
          <div v-if="form.status === 'finalised' && loan.finaliser" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.finalisation_info') }}</h3>
            <div class="bg-purple-50 p-4 rounded-md">
              <p class="text-gray-700">
                {{ $t('message.loan.finalised_by') }}: <span class="font-semibold">{{ loan.finaliser.name }}</span>
              </p>
              <p v-if="loan.finalised_at" class="text-gray-700">
                {{ $t('message.loan.finalised_at') }}: <span class="font-semibold">{{ formatDate(loan.finalised_at) }}</span>
              </p>
            </div>
          </div>

          <!-- Rejection Information -->
          <div v-if="form.status === 'rejected' && loan.rejecter" class="pb-8 pr-6 w-full">
            <h3 class="mb-2 text-lg font-bold">{{ $t('message.loan.rejection_info') }}</h3>
            <div class="p-4 bg-red-50 rounded-md">
              <p class="text-gray-700">
                {{ $t('message.loan.rejected_by') }}: <span class="font-semibold">{{ loan.rejecter.name }}</span>
              </p>
              <p v-if="form.rejection_reason" class="mt-2 text-gray-700">
                {{ $t('message.loan.rejection_reason') }}: <span class="font-semibold">{{ form.rejection_reason }}</span>
              </p>
            </div>
          </div>

          <!-- Eligibility Information -->
          <div class="bg-blue-50 border-blue-200 mb-6 p-4 border rounded-md">
            <h2 class="text-blue-800 mb-2 text-lg font-semibold">Loan Eligibility Requirements</h2>
            <ul class="text-blue-700 pl-5 list-disc">
              <li>You must have at least 3 years of service with Nafa to be eligible for a loan.</li>
              <li>Monthly loan repayment cannot exceed 40% of your net salary.</li>
              <li>Loan should be repaid within 24 months of disbursement.</li>
            </ul>
          </div>

          <!-- Error message for eligibility -->
          <div v-if="$page.props.errors.eligibility" class="mb-6 p-4 bg-red-100 border border-red-300 rounded-md">
            <p class="text-red-700">{{ $page.props.errors.eligibility }}</p>
          </div>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100">
          <button v-if="!loan.deleted_at && can_edit" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">
            {{ $t('message.loan.delete_loan') }}
          </button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">
            {{ $t('message.loan.update_loan') }}
          </loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import SelectInput from '@/Shared/SelectInput'
import DatePicker from '@/Shared/DatePicker'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
  components: {
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
    DatePicker,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    loan: Object,
    loan_types: Array,
    branches: Array,
    levels: Array,
    can_approve: Boolean,
    can_edit: Boolean,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        loan_type_id: this.loan.loan_type_id,
        branch: this.loan.branch,
        level: this.loan.level,
        net_salary: this.loan.net_salary,
        loan_amount: this.loan.loan_amount,
        loan_tenure: this.loan.loan_tenure,
        purpose: this.loan.purpose,
        status: this.loan.status,
        disbursement_date: this.loan.disbursement_date,
        rejection_reason: this.loan.rejection_reason,
        outstanding_nafa_loan_balance: this.loan.outstanding_nafa_loan_balance,
        outstanding_bank_loan_balance: this.loan.outstanding_bank_loan_balance,
        other_loans_with_nafa_id: this.loan.other_loans_with_nafa_id,
        other_loans_with_bank: this.loan.other_loans_with_bank,
        wallet_number: this.loan.wallet_number,
        // funds_transfer_status: this.loan.funds_transfer_status,
        // funds_received_at: this.loan.funds_received_at,
      }),
      minLoanAmount: 0,
      maxLoanAmount: Number.MAX_SAFE_INTEGER,
      maxEligibleLoanAmount: 0,
    }
  },
  computed: {
    selectedLoanType() {
      if (!this.form.loan_type_id) return null
      return this.loan_types.find((lt) => lt.id === this.form.loan_type_id)
    },
    canCalculatePayment() {
      return this.form.loan_type_id && this.form.loan_amount > 0 && this.form.loan_tenure > 0
    },
    monthlyPayment() {
      if (!this.canCalculatePayment) return 0

      const principal = parseFloat(this.form.loan_amount)
      const rate = this.selectedLoanType.interest_rate / 100 / 12 // Monthly interest rate
      const term = parseInt(this.form.loan_tenure) // Term in months

      // If interest rate is 0, simple division
      if (rate === 0) {
        return principal / term
      }

      // PMT formula: PMT = P * (r(1+r)^n) / ((1+r)^n - 1)
      const monthlyPayment = (principal * (rate * Math.pow(1 + rate, term))) / (Math.pow(1 + rate, term) - 1)

      return Math.round(monthlyPayment * 100) / 100
    },
    totalPayment() {
      return Math.round(this.monthlyPayment * this.form.loan_tenure * 100) / 100
    },
    totalInterest() {
      return Math.round((this.totalPayment - this.form.loan_amount) * 100) / 100
    },
    today() {
      return new Date().toISOString().split('T')[0]
    },
    repaymentPercentage() {
      if (this.form.net_salary > 0) {
        return (this.monthlyPayment / this.form.net_salary) * 100
      }
      return 0
    },
    // Check if user can review loans (HR or admin)
    canReview() {
      return (this.$page.props.auth.user.roles.includes('hr') || this.$page.props.auth.user.roles.includes('admin')) && ['pending'].includes(this.loan.status)
    },
    // Check if user can approve loans (Exec or admin)
    canApprove() {
      return (this.$page.props.auth.user.roles.includes('exec') || this.$page.props.auth.user.roles.includes('admin')) && ['reviewed'].includes(this.loan.status)
    },
    // Check if user can finalise loans (Head of HR or admin)
    canFinalise() {
      return (this.$page.props.auth.user.roles.includes('hr') || this.$page.props.auth.user.roles.includes('admin')) && ['approved'].includes(this.loan.status)
    },
  },
  mounted() {
    this.updateLoanLimits()
    this.updateMaxEligibleAmount()
  },
  methods: {
    updateLoanLimits() {
      if (this.selectedLoanType) {
        this.minLoanAmount = this.selectedLoanType.min_loan_amount
        this.maxLoanAmount = this.selectedLoanType.max_loan_amount

        // Adjust loan amount if outside limits
        if (this.form.loan_amount < this.minLoanAmount) {
          this.form.loan_amount = this.minLoanAmount
        } else if (this.form.loan_amount > this.maxLoanAmount) {
          this.form.loan_amount = this.maxLoanAmount
        }

        this.updateMaxEligibleAmount()
      } else {
        this.minLoanAmount = 0
        this.maxLoanAmount = Number.MAX_SAFE_INTEGER
        this.maxEligibleLoanAmount = 0
      }
    },
    updateMaxEligibleAmount() {
      if (!this.selectedLoanType || this.form.net_salary <= 0 || this.form.loan_tenure <= 0) {
        this.maxEligibleLoanAmount = 0
        return
      }

      const netSalary = parseFloat(this.form.net_salary)
      const maxMonthlyPayment = netSalary * 0.4
      const rate = this.selectedLoanType.interest_rate / 100 / 12 // Monthly interest rate
      const term = parseInt(this.form.loan_tenure)

      // If interest rate is 0, simple multiplication
      if (rate === 0) {
        this.maxEligibleLoanAmount = maxMonthlyPayment * term
      } else {
        // Reverse PMT formula: P = PMT * ((1+r)^n - 1) / (r(1+r)^n)
        this.maxEligibleLoanAmount = (maxMonthlyPayment * (Math.pow(1 + rate, term) - 1)) / (rate * Math.pow(1 + rate, term))
      }

      this.maxEligibleLoanAmount = Math.round(this.maxEligibleLoanAmount * 100) / 100

      // Cap the max eligible amount to the loan type's max amount
      if (this.selectedLoanType && this.maxEligibleLoanAmount > this.selectedLoanType.max_loan_amount) {
        this.maxEligibleLoanAmount = this.selectedLoanType.max_loan_amount
      }
    },
    formatCurrency(amount) {
      return new Intl.NumberFormat('en-GM', {
        style: 'currency',
        currency: 'GMD',
      }).format(amount)
    },
    formatDate(dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleString()
    },
    update() {
      this.form.put(`/loans/loans/${this.loan.id}`)
    },
    destroy() {
      if (confirm(this.$t('message.general.are_you_sure'))) {
        this.$inertia.delete(`/loans/loans/${this.loan.id}`)
      }
    },
    restore() {
      if (confirm(this.$t('message.general.are_you_sure'))) {
        this.$inertia.put(`/loans/loans/${this.loan.id}/restore`)
      }
    },
    validateWalletNumber() {
      // Remove any non-digit characters
      this.form.wallet_number = this.form.wallet_number.replace(/\D/g, '')

      // Ensure it's not longer than 7 digits
      if (this.form.wallet_number.length > 7) {
        this.form.wallet_number = this.form.wallet_number.substring(0, 7)
      }
    },
  },
}
</script>
